/**
 * needle-tween.ts — juice-it style spring kicks on the compass needle.
 *
 * Watches the frame-bus drums channel for rising-edge "peaks" (level
 * climbs past a threshold, then we wait for it to fall back below a
 * lower threshold before allowing the next peak — hysteresis). On each
 * detected peak we bump the needle's --needle-kick custom property by
 * a randomised amount; CSS handles the spring overshoot via
 * `transition: --needle-kick 720ms cubic-bezier(.34, 1.56, .64, 1)`.
 *
 * Net feel: needle drifts hypnotically between beats, then SNAPS to a
 * new angle with juicy overshoot on each drum hit. Replaces the prior
 * "spin-faster-when-loud" continuous animation.
 */

import { getStem } from "./frame-bus.ts";

const PEAK_THRESHOLD = 0.55;     // rising edge: level must cross this
const RESET_THRESHOLD = 0.30;    // falling edge: level must drop below this before next peak
const MIN_KICK_DEG = 12;
const MAX_KICK_DEG = 38;
const MIN_INTERVAL_MS = 220;     // hard floor: even if hysteresis triggers earlier, no peak inside this window

let armed = true;
let lastKickAt = 0;
let kickDeg = 0;
let rafId: number | null = null;
let needle: SVGElement | null = null;

const findNeedle = (): SVGElement | null => {
  if (needle && needle.isConnected) return needle;
  needle = document.querySelector(".compass-needle--mana");
  return needle;
};

const tick = (now: number): void => {
  const el = findNeedle();
  if (el !== null) {
    const drums = getStem("drums");
    const sinceLast = now - lastKickAt;

    if (armed && drums >= PEAK_THRESHOLD && sinceLast >= MIN_INTERVAL_MS) {
      // Rising-edge peak detected. Bump kick by a randomised amount,
      // direction sometimes reversed for variety.
      const magnitude = MIN_KICK_DEG + Math.random() * (MAX_KICK_DEG - MIN_KICK_DEG);
      const direction = Math.random() < 0.18 ? -1 : 1;     // mostly forward, occasionally reverse
      kickDeg += magnitude * direction;
      // Keep kickDeg bounded to avoid float drift over long sessions.
      if (kickDeg > 360 || kickDeg < -360) kickDeg = kickDeg % 360;
      el.style.setProperty("--needle-kick", `${kickDeg.toFixed(2)}deg`);
      armed = false;
      lastKickAt = now;
    } else if (!armed && drums < RESET_THRESHOLD) {
      // Drums fell back — re-arm for next peak.
      armed = true;
    }
  }
  rafId = requestAnimationFrame(tick);
};

const start = (): void => {
  if (rafId !== null) return;
  rafId = requestAnimationFrame(tick);
};

const stop = (): void => {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
};

export const registerNeedleTween = (): void => {
  start();
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stop();
    else start();
  });
};
